import { useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, Download, Paperclip, Trash2, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useFetch } from "@/hooks/useFetch";
import { api, downloadFile, errMsg } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { useMe } from "@/components/app/MeContext";
import { Progress } from "@/components/ui/progress";
import { LARGE_FILE, uploadInParts } from "@/lib/chunkedUpload";

const TYPES = ["PO", "Drawing", "Spec", "Datasheet", "Quotation", "Invoice", "Delivery Challan", "E-way Bill", "LR Copy", "POD",
  "GRN", "Test Report", "Contract", "Handover Certificate", "Commissioning Report", "Warranty Certificate", "Manual", "Other"];

const DEFAULT_TYPE = { dispatch: "LR Copy", movement: "Transporter Invoice", receipt: "GRN" };
const opt = (t) => (typeof t === "string" ? { value: t, label: t } : t);

// File picker + camera capture (phones open the camera directly; desktops fall back to a file picker)
function FilePick({ file, setFile, inputs }) {
  const pick = (e) => setFile(e.target.files?.[0] || null);
  return (
    <div className="flex flex-wrap items-center gap-2 basis-full md:basis-auto flex-1 min-w-0">
      <input ref={inputs.file} type="file" className="hidden" onChange={pick} data-testid="doc-file-input" />
      <input ref={inputs.cam} type="file" accept="image/*" capture="environment" className="hidden" onChange={pick} data-testid="doc-camera-input" />
      <Button type="button" size="sm" variant="outline" className="bg-white" onClick={() => inputs.file.current.click()} data-testid="doc-choose-file-btn"><Paperclip className="h-4 w-4 mr-1" />Choose file</Button>
      <Button type="button" size="sm" variant="outline" className="bg-white" onClick={() => inputs.cam.current.click()} data-testid="doc-take-photo-btn"><Camera className="h-4 w-4 mr-1" />Take photo</Button>
      <span className="text-xs text-slate-600 truncate max-w-full" data-testid="doc-picked-name">{file ? file.name : "No file chosen"}</span>
    </div>
  );
}

function InvoicePick({ invoices, value, onChange, testid }) {
  return (
    <Select value={value || "__none"} onValueChange={(v) => onChange(v === "__none" ? "" : v)}>
      <SelectTrigger className="md:w-48 bg-white h-9 text-xs" data-testid={testid}><SelectValue /></SelectTrigger>
      <SelectContent><SelectItem value="__none">Not linked to an invoice</SelectItem>
        {invoices.map((i) => <SelectItem key={i.id} value={i.id}>{i.invoice_no}{i.status === "cancelled" ? " (cancelled)" : ""}</SelectItem>)}</SelectContent>
    </Select>
  );
}

// Upload state + action (small files in one request, large ones in parts with progress)
function useDocUpload({ entityType, entityId, onlyType, typeButtons, fixedInvoice, defaultTitle, done }) {
  const [invoiceId, setInvoiceId] = useState(fixedInvoice || "");
  const [docType, setDocType] = useState(onlyType || typeButtons?.[0]?.[0] || DEFAULT_TYPE[entityType] || "PO");
  const [title, setTitle] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pct, setPct] = useState(null);
  const inputs = { file: useRef(null), cam: useRef(null) };
  const send = async (meta) => {
    if (file.size <= LARGE_FILE) {
      const fd = new FormData();
      Object.entries(meta).forEach(([k, v]) => fd.append(k, v));
      fd.append("file", file);
      return (await api.post("/documents", fd)).data;
    }
    setPct(0);
    return uploadInParts({ file, meta, onProgress: setPct });
  };
  const reset = () => {
    setTitle("");
    setFile(null);
    if (!fixedInvoice) setInvoiceId("");
    Object.values(inputs).forEach((r) => { if (r.current) r.current.value = ""; });
  };
  const upload = async () => {
    if (!file) return toast.error("Choose a file or take a photo first");
    setBusy(true);
    try {
      const saved = await send({ entity_type: entityType, entity_id: entityId, doc_type: docType, title: title || defaultTitle, invoice_id: invoiceId });
      toast.success(`Saved as ${saved.stored_name}`);
      reset();
      done();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
      setPct(null);
    }
  };
  return { invoiceId, setInvoiceId, docType, setDocType, title, setTitle, file, setFile, busy, pct, inputs, upload };
}

export function DocumentsPanel({ entityType, entityId, onChange, types: fixedTypes, onlyType, typeButtons, uploadLabel = "Upload", titlePlaceholder = "Title (optional)", invoices, invoiceId: fixedInvoice, defaultTitle = "", hideList = false }) {
  const { can } = useMe();
  const { data: all, reload } = useFetch(`/documents?entity_type=${entityType}&entity_id=${entityId}`);
  const { data: fetched } = useFetch(fixedTypes ? null : `/documents/types?entity_type=${entityType}`);
  const types = (fixedTypes || fetched || TYPES).map(opt);
  const data = (onlyType ? (all || []).filter((d) => d.doc_type === onlyType) : all)?.filter((d) => !fixedInvoice || d.invoice_id === fixedInvoice);
  const up = useDocUpload({ entityType, entityId, onlyType, typeButtons, fixedInvoice, defaultTitle, done: () => { reload(); onChange?.(); } });
  const { invoiceId, setInvoiceId, docType, setDocType, title, setTitle, file, setFile, busy, pct, inputs, upload } = up;

  const relink = async (d, v) => {
    try { await api.post(`/documents/${d.id}/invoice-link`, { invoice_id: v }); toast.success(v ? "Linked to invoice" : "Unlinked"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };

  const remove = async (d) => {
    if (!window.confirm(`Remove ${d.stored_name}?`)) return;
    try {
      await api.delete(`/documents/${d.id}`);
      reload();
      onChange?.();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="space-y-4" data-testid="documents-panel">
      {can("write") && (
        <div className="flex flex-col md:flex-row flex-wrap gap-2 md:items-center p-3 rounded border border-dashed border-slate-300 bg-slate-50">
          {typeButtons && <div className="flex flex-wrap gap-1.5 basis-full" data-testid="doc-type-buttons">{typeButtons.map(([v, l]) => (
            <button key={v} type="button" onClick={() => setDocType(v)} data-testid={`doc-type-btn-${v.replace(/\W+/g, "-").toLowerCase()}`}
              className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${docType === v ? "border-sky-700 bg-sky-700 text-white" : "border-slate-300 bg-white text-slate-700 hover:border-sky-500"}`}>{l}</button>))}</div>}
          {!onlyType && !typeButtons && <Select value={docType} onValueChange={setDocType}>
            <SelectTrigger className="md:w-48 bg-white" data-testid="doc-type-select"><SelectValue /></SelectTrigger>
            <SelectContent>{types.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
          </Select>}
          <Input placeholder={titlePlaceholder} value={title} onChange={(e) => setTitle(e.target.value)} className="md:w-48 bg-white" data-testid="doc-title-input" />
          {invoices && !fixedInvoice && <InvoicePick invoices={invoices.filter((i) => i.status !== "cancelled")} value={invoiceId} onChange={(v) => { setInvoiceId(v); const i = invoices.find((x) => x.id === v); if (i && !title) setTitle(i.invoice_no); }} testid="doc-invoice-select" />}
          <FilePick file={file} setFile={setFile} inputs={inputs} />
          <Button size="sm" onClick={upload} disabled={busy} data-testid="doc-upload-btn">
            <Upload className="h-4 w-4 mr-1" /> {busy ? (pct !== null ? `${pct}%` : "Uploading…") : uploadLabel}
          </Button>
        </div>
      )}
      {pct !== null && <div className="space-y-1" data-testid="doc-upload-progress"><Progress value={pct} className="h-2" /><p className="text-xs text-slate-500">Uploading large file in parts — {pct}%. Keep this page open.</p></div>}
      {!hideList && <>
      <p className="text-xs text-slate-500">Files are renamed to a standard pattern: RECORD_TYPE_DATE_vN. The original file name is kept for reference. Videos (MP4, MOV, MKV, AVI) up to 2 GB; other files up to 25 MB.</p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="th text-left"><th className="px-3 py-2">File</th><th className="px-3 py-2">Type</th><th className="px-3 py-2">Original name</th>{invoices && !fixedInvoice && <th className="px-3 py-2">Invoice</th>}<th className="px-3 py-2">Uploaded</th><th className="px-3 py-2"></th></tr></thead>
          <tbody>
            {(data || []).map((d) => (
              <tr key={d.id} className="border-b border-slate-100" data-testid={`doc-row-${d.id}`}>
                <td className="px-3 py-2 min-w-[180px]"><div className="font-mono text-xs text-slate-900 break-all">{d.stored_name}{d.linked_from && <span className="ml-1 rounded bg-sky-100 px-1.5 py-0.5 font-sans text-[10px] font-semibold text-sky-800" data-testid={`doc-from-${d.id}`}>From {d.linked_from}</span>}</div><div className="text-xs text-slate-500">{d.title !== d.stored_name ? d.title : ""}</div></td>
                <td className="px-3 py-2 text-xs">{types.find((t) => t.value === d.doc_type)?.label || d.doc_type} · v{d.version}</td>
                <td className="px-3 py-2 text-xs text-slate-500 break-all">{d.original_filename}</td>
                {invoices && !fixedInvoice && <td className="px-3 py-2">{can("documents.create") ? <InvoicePick invoices={invoices.filter((i) => i.status !== "cancelled")} value={d.invoice_id || ""} onChange={(v) => relink(d, v)} testid={`doc-invoice-link-${d.id}`} /> : <span className="font-mono text-xs">{d.invoice_no || "—"}</span>}</td>}
                <td className="px-3 py-2 text-xs text-slate-500">{d.uploaded_by?.name}<br />{fmtDateTime(d.uploaded_at)}</td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  <Button variant="ghost" size="icon" onClick={() => downloadFile(`/documents/${d.id}/download`, d.stored_name).catch((e) => toast.error(errMsg(e)))} data-testid={`doc-download-${d.id}`}><Download className="h-4 w-4" /></Button>
                  {can("documents.delete") && !d.linked_from && <Button variant="ghost" size="icon" onClick={() => remove(d)} data-testid={`doc-remove-${d.id}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>}
                </td>
              </tr>
            ))}
            {data && data.length === 0 && <tr><td colSpan={6} className="px-3 py-6 text-center text-sm text-slate-500">No documents yet</td></tr>}
          </tbody>
        </table>
      </div>
      </>}
    </div>
  );
}
